import { EventName } from "module/analytics/types"

import { ChevronDownIcon } from "@chakra-ui/icons"
import {
    Box,
    Button,
    HStack,
    Icon,
    Link,
    Menu,
    MenuButton,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Portal,
    Text,
    VStack,
} from "@chakra-ui/react"
import { getLeastSignificantDigit } from "@perp/sdk-curie"
import Big from "big.js"
import { LimitOrderIcon, MarketOrderIcon, TriggerOrderIcon } from "component/Icon"
import { PerpNumberInput } from "component/PerpNumberInput/PerpNumberInput"
import { TextLabel } from "component/Typography"
import { BASE_ASSET_ADVANCED_PRECISION, USD_PRECISION } from "constant/number"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { RiQuestionLine } from "react-icons/ri"
import { usePositionDraftDetail } from "sdk-react/clearingHouse/usePositionDraftDetail"
import { OrderType } from "sdk-react/limitOrder/types"

import { CreateOrder } from "../../hook/useCreateOrder"

type OrderTypeSelectProps = {
    orderType: OrderType
    updateOrderType: (orderType: OrderType) => void
    isStopOrderSupported: boolean
}
const OrderTypeSelect = ({ orderType, updateOrderType, isStopOrderSupported }: OrderTypeSelectProps) => {
    const { t } = useTranslation()
    const OptionDisplayMap: Record<OrderType, ReactNode> = {
        [OrderType.MARKET]: t("general.order_type_market"),
        [OrderType.LIMIT]: t("general.order_type_limit"),
        [OrderType.STOP_LIMIT]: t("general.order_type_stop_limit"),
        [OrderType.TAKE_PROFIT_LIMIT]: t("general.order_type_take_profit_limit"),
    }

    const IconDisplayMap = {
        [OrderType.MARKET]: <Icon color="orange.400" mr={1} boxSize={"24px"} as={MarketOrderIcon} />,
        [OrderType.LIMIT]: <Icon color="blue.400" mr={1} boxSize={"24px"} as={LimitOrderIcon} />,
        [OrderType.STOP_LIMIT]: <Icon color="teal.400" mr={1} boxSize={"24px"} as={TriggerOrderIcon} />,
        [OrderType.TAKE_PROFIT_LIMIT]: <Icon color="teal.400" mr={1} boxSize={"24px"} as={TriggerOrderIcon} />,
    }

    return (
        <VStack spacing={2} align="stretch" w="50%" flexShrink={0}>
            <HStack h="18px">
                <Text variant="label">Order Type</Text>

                {orderType === OrderType.LIMIT && (
                    <Link target="_blank" href="https://support.perp.com/hc/en-us/articles/7270204698009">
                        <Icon as={RiQuestionLine} boxSize="16px" position="relative" top={"2px"} opacity={0.65} />
                    </Link>
                )}
                {orderType === OrderType.STOP_LIMIT && (
                    <Link
                        target="_blank"
                        href="https://support.perp.com/hc/en-us/articles/7653878375833-What-is-a-Stop-Loss-Order-"
                    >
                        <Icon as={RiQuestionLine} boxSize="16px" position="relative" top={"2px"} opacity={0.65} />
                    </Link>
                )}
            </HStack>

            <Menu>
                <MenuButton
                    width={"100%"}
                    as={Button}
                    variant="outline"
                    minHeight={"auto"}
                    fontSize="sm"
                    textAlign={"left"}
                    fontWeight={"normal"}
                    px={2}
                    rightIcon={<ChevronDownIcon />}
                >
                    <HStack justify={"flex-start"} spacing={1}>
                        {IconDisplayMap[orderType]}
                        <Text noOfLines={1}>{OptionDisplayMap[orderType]}</Text>
                    </HStack>
                </MenuButton>
                <Portal>
                    <MenuList>
                        <MenuOptionGroup
                            type="radio"
                            value={orderType}
                            onChange={value => updateOrderType(value as OrderType)}
                        >
                            <MenuItemOption command="M" value={OrderType.MARKET} fontSize="sm">
                                <Icon color="orange.400" mr={1} boxSize={"24px"} as={MarketOrderIcon} />{" "}
                                {t("general.order_type_market")}
                            </MenuItemOption>
                            <MenuItemOption command="L" value={OrderType.LIMIT} fontSize="sm">
                                <Icon color="blue.400" mr={1} boxSize={"24px"} as={LimitOrderIcon} />{" "}
                                {t("general.order_type_limit")}
                            </MenuItemOption>
                            <MenuItemOption
                                command="S"
                                value={OrderType.STOP_LIMIT}
                                fontSize="sm"
                                isDisabled={!isStopOrderSupported}
                            >
                                <HStack>
                                    <Icon color="teal.400" mr={1} boxSize={"24px"} as={TriggerOrderIcon} />{" "}
                                    <Box pr={2}>
                                        <Box>{t("general.order_type_stop_limit")}</Box>
                                        {!isStopOrderSupported && <TextLabel>Unavailable for this market</TextLabel>}
                                    </Box>
                                </HStack>
                            </MenuItemOption>
                        </MenuOptionGroup>
                    </MenuList>
                </Portal>
            </Menu>
        </VStack>
    )
}

type OrderTypePriceInputProps = {
    marketPrice?: Big
}
export const OrderTypePriceInput = ({ marketPrice }: OrderTypePriceInputProps) => {
    const {
        orderType,
        updateOrderType,
        isOrderTypeMarket,
        priceString,
        updatePrice,
        triggerPriceString,
        updateTriggerPrice,
        isStopOrderSupported,
    } = CreateOrder.useContainer()
    const { entryPrice } = usePositionDraftDetail()
    const entryPriceString = entryPrice?.toString()

    const { t } = useTranslation()

    const precision = marketPrice
        ? getLeastSignificantDigit(marketPrice, BASE_ASSET_ADVANCED_PRECISION, USD_PRECISION)
        : BASE_ASSET_ADVANCED_PRECISION

    switch (orderType) {
        case OrderType.MARKET:
        case OrderType.LIMIT: {
            const priceInputValue = isOrderTypeMarket && entryPriceString ? entryPriceString : priceString
            return (
                <VStack align="stretch" w="100%">
                    <HStack spacing={2} align="stretch" w="100%">
                        <VStack spacing={2} align="stretch" w="100%">
                            <Text variant="label">{t("general.price")}</Text>
                            <PerpNumberInput
                                isDisabled={isOrderTypeMarket}
                                value={priceInputValue}
                                precision={precision}
                                placeholder={"Enter Price"}
                                onChange={updatePrice}
                                trackingProps={{
                                    eventName: EventName.POSITION_OPEN_PRICE_UPDATED,
                                    payload: { price: priceString || "" },
                                }}
                            />
                        </VStack>
                        <OrderTypeSelect
                            orderType={orderType}
                            updateOrderType={updateOrderType}
                            isStopOrderSupported={isStopOrderSupported}
                        />
                    </HStack>
                </VStack>
            )
        }
        case OrderType.STOP_LIMIT:
        case OrderType.TAKE_PROFIT_LIMIT: {
            return (
                <VStack align="stretch" w="100%" spacing={6}>
                    <HStack spacing={2} align="stretch" w="100%">
                        <VStack spacing={2} align="stretch" w="100%">
                            <Text variant="label">{t("general.trigger_price")}</Text>
                            <PerpNumberInput
                                value={triggerPriceString}
                                precision={precision}
                                placeholder={"Enter Price"}
                                onChange={updateTriggerPrice}
                                trackingProps={{
                                    eventName: EventName.POSITION_OPEN_TRIGGER_PRICE_UPDATED,
                                    payload: { price: triggerPriceString || "" },
                                }}
                            />
                        </VStack>
                        <OrderTypeSelect
                            orderType={orderType}
                            updateOrderType={updateOrderType}
                            isStopOrderSupported={isStopOrderSupported}
                        />
                    </HStack>
                    <HStack spacing={2} align="stretch" w="100%">
                        <VStack spacing={2} align="stretch" w="100%">
                            <Text variant="label">{t("general.limit_price")}</Text>
                            <PerpNumberInput
                                value={priceString}
                                precision={precision}
                                placeholder="Enter Price"
                                onChange={updatePrice}
                                trackingProps={{
                                    eventName: EventName.POSITION_OPEN_LIMIT_PRICE_UPDATED,
                                    payload: { price: priceString || "" },
                                }}
                            />
                        </VStack>
                    </HStack>{" "}
                </VStack>
            )
        }
        default:
            return null
    }
}
